import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { MdBusiness, MdExpandMore, MdCheck } from "react-icons/md";
import { useBranding } from "../../contexts/BrandingContext";

const BASE = (process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "");
const api = (path) => `${BASE}/${path}`;

const SuperAdminOrgPicker = ({ collapsed }) => {
  const [orgs, setOrgs] = useState([]);
  const [activeOrgId, setActiveOrgId] = useState(null);
  const [activeOrgName, setActiveOrgName] = useState("");
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const dropdownRef = useRef(null);
  const { refetchBranding } = useBranding();

  useEffect(() => {
    fetchOrgs();
    fetchCurrentOrg();
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const fetchOrgs = async () => {
    try {
      const res = await axios.get(api("superAdmin/organizations"));
      setOrgs(res.data?.result?.organizations || []);
    } catch (e) {
      console.error("SuperAdmin: failed to fetch org list", e);
    }
  };

  const fetchCurrentOrg = async () => {
    try {
      const res = await axios.get(api("superAdmin/current_org"));
      const org = res.data?.result?.organization;
      if (org?.organizationId) {
        setActiveOrgId(org.organizationId);
        setActiveOrgName(org.organizationName || "");
      }
    } catch {
      // No active org yet — expected on first login
    }
  };

  // Drop every per-org cache so the next view rebuilds for the new context.
  const clearPerOrgCaches = () => {
    try {
      const ls = window.localStorage;
      for (let i = ls.length - 1; i >= 0; i--) {
        const k = ls.key(i);
        if (!k) continue;
        if (/^(gis_|road_|defect_)/.test(k) || k === "ro_filter" || k === "piu_filter" || k === "brandingSettings" || k === "roadNameEdits") {
          ls.removeItem(k);
        }
      }
      const ss = window.sessionStorage;
      for (let i = ss.length - 1; i >= 0; i--) {
        const k = ss.key(i);
        if (k && /^gis_/.test(k)) ss.removeItem(k);
      }
      window.dispatchEvent(new CustomEvent("roadvision:logout"));
    } catch {}
  };

  const handleSwitch = async (org) => {
    if (switching || activeOrgId === org.organizationId) {
      setOpen(false);
      return;
    }
    setSwitching(true);
    try {
      await axios.post(
        api("superAdmin/switch_org"),
        { organizationId: org.organizationId },
        { headers: { "Content-Type": "application/json" } }
      );

      setActiveOrgId(org.organizationId);
      setActiveOrgName(org.organizationName || "");
      setOpen(false);

      // Update localStorage so dashboard/map components pick up new org
      localStorage.setItem("organization", org.organizationName || "");
      localStorage.setItem("city", org.organizationCity || org.organizationName || "");

      clearPerOrgCaches();

      // Reload branding for the new org, then navigate to dashboard
      await refetchBranding();
      window.location.href = "/home";
    } catch (e) {
      console.error("SuperAdmin: org switch failed", e);
    } finally {
      setSwitching(false);
    }
  };

  // "No Organization" — clear the active org so org-aware views (the GIS map)
  // show data across ALL organizations.
  const handleClearOrg = async () => {
    if (switching || activeOrgId === null) {
      setOpen(false);
      return;
    }
    setSwitching(true);
    try {
      await axios.post(
        api("superAdmin/clear_org"),
        {},
        { headers: { "Content-Type": "application/json" } }
      );

      setActiveOrgId(null);
      setActiveOrgName("");
      setOpen(false);

      // No specific org context anymore.
      localStorage.removeItem("organization");
      localStorage.removeItem("city");

      clearPerOrgCaches();

      // Land on the GIS map, which is where the all-organizations view is shown.
      await refetchBranding();
      window.location.href = "/pavement/multiMap";
    } catch (e) {
      console.error("SuperAdmin: clear org failed", e);
    } finally {
      setSwitching(false);
    }
  };

  if (collapsed) {
    return (
      <div className="flex justify-center py-2 border-b border-white/10">
        <div
          title={activeOrgName || "Select org"}
          className="w-7 h-7 rounded-full bg-blue-500/30 flex items-center justify-center"
          style={{ color: "#93c5fd" }}
        >
          <MdBusiness size={14} />
        </div>
      </div>
    );
  }

  return (
    <div className="px-3 py-2 border-b border-white/10" ref={dropdownRef}>
      <div style={{ fontSize: 9, fontWeight: 600, letterSpacing: "0.1em", color: "rgba(255,255,255,0.4)", textTransform: "uppercase", marginBottom: 6 }}>
        Active Organization
      </div>

      <button
        onClick={() => setOpen((v) => !v)}
        disabled={switching}
        style={{
          width: "100%", display: "flex", alignItems: "center", gap: 8,
          padding: "6px 10px", borderRadius: 8, background: "rgba(255,255,255,0.1)",
          color: "white", textAlign: "left", border: "none", cursor: "pointer",
          opacity: switching ? 0.6 : 1, transition: "background 0.15s",
        }}
        onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.15)"}
        onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.1)"}
      >
        <MdBusiness size={14} style={{ flexShrink: 0, color: "#93c5fd" }} />
        <span style={{ flex: 1, fontSize: 12, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {switching ? "Switching…" : (activeOrgName || "All Organizations")}
        </span>
        <MdExpandMore
          size={16}
          style={{ flexShrink: 0, transition: "transform 0.2s", transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
        />
      </button>

      {open && (
        <div style={{
          marginTop: 4, borderRadius: 8, background: "#0f2540",
          border: "1px solid rgba(255,255,255,0.1)",
          maxHeight: 208, overflowY: "auto", zIndex: 50,
        }}>
          {/* "No Organization" — clears the active org to show all orgs on the GIS map */}
          <button
            onClick={handleClearOrg}
            style={{
              width: "100%", display: "flex", alignItems: "center", gap: 8,
              padding: "8px 12px", textAlign: "left", border: "none",
              borderBottom: "1px solid rgba(255,255,255,0.08)",
              background: activeOrgId === null ? "rgba(59,130,246,0.2)" : "transparent",
              cursor: "pointer", transition: "background 0.15s",
            }}
            onMouseEnter={e => { if (activeOrgId !== null) e.currentTarget.style.background = "rgba(255,255,255,0.08)"; }}
            onMouseLeave={e => { if (activeOrgId !== null) e.currentTarget.style.background = "transparent"; }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 500, color: activeOrgId === null ? "#bfdbfe" : "rgba(255,255,255,0.85)" }}>
                No Organization
              </div>
              <div style={{ fontSize: 10, color: "rgba(255,255,255,0.4)" }}>
                Show all organizations
              </div>
            </div>
            {activeOrgId === null && (
              <MdCheck size={14} style={{ flexShrink: 0, color: "#93c5fd" }} />
            )}
          </button>

          {orgs.length === 0 ? (
            <div style={{ padding: "12px", fontSize: 11, color: "rgba(255,255,255,0.4)", textAlign: "center" }}>
              No organizations found
            </div>
          ) : (
            orgs.map((org) => {
              const isActive = activeOrgId === org.organizationId;
              return (
                <button
                  key={org.organizationId}
                  onClick={() => handleSwitch(org)}
                  style={{
                    width: "100%", display: "flex", alignItems: "flex-start", gap: 8,
                    padding: "8px 12px", textAlign: "left", border: "none",
                    background: isActive ? "rgba(59,130,246,0.2)" : "transparent",
                    cursor: "pointer", transition: "background 0.15s",
                  }}
                  onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = "rgba(255,255,255,0.08)"; }}
                  onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = "transparent"; }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 500, color: isActive ? "#bfdbfe" : "rgba(255,255,255,0.85)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {org.organizationName}
                    </div>
                    {org.organizationCity && (
                      <div style={{ fontSize: 10, color: "rgba(255,255,255,0.4)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {org.organizationCity}
                      </div>
                    )}
                  </div>
                  {isActive && (
                    <MdCheck size={14} style={{ flexShrink: 0, color: "#93c5fd", marginTop: 2 }} />
                  )}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default SuperAdminOrgPicker;
